import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { formatMoney } from '../utils/formatMoney';

const advLabelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '600',
  color: 'var(--color-neutral-text-muted)',
  marginBottom: '6px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const advInputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-ui)',
  border: '1px solid var(--color-border)',
  fontSize: '14px',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-surface)',
  color: 'var(--color-neutral-text-main)',
};

const criteriaPanelStyle = {
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-ui)',
  padding: '16px',
  backgroundColor: 'var(--color-surface)',
  marginBottom: '16px',
};

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'year', 'month', 'dateRange'
  /** Sidebar: all | zero | nonzero — uses running balance from API */
  const [balanceFilter, setBalanceFilter] = useState('all');
  const [balanceMin, setBalanceMin] = useState('');
  const [balanceMax, setBalanceMax] = useState('');
  /** contains = partial match (default); exact = whole file no. */
  const [fileNoMatchMode, setFileNoMatchMode] = useState('contains');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/ledger/accounts`);
      setAccounts(response.data.accounts);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountDetails = async (fileNo) => {
    setDetailsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();

      if (filterType === 'year' && year) {
        params.append('year', year);
      } else if (filterType === 'month' && year && month) {
        params.append('year', year);
        params.append('month', month);
      } else if (filterType === 'dateRange') {
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
      }

      const response = await axios.get(
        `${API_URL}/api/ledger/account/${encodeURIComponent(fileNo)}?${params.toString()}`
      );
      setAccountDetails(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch account details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
    fetchAccountDetails(account.fileNo);
  };

  const handleFilterChange = () => {
    if (selectedAccount) {
      fetchAccountDetails(selectedAccount.fileNo);
    }
  };

  const isZeroBalance = (balance) => Math.round(Number(balance) * 100) === 0;

  const parseOptionalAmount = (raw) => {
    const t = String(raw).trim().replace(/,/g, '');
    if (t === '') return null;
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : null;
  };

  const accountMatchesSidebarFilters = useCallback(
    (account) => {
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        const fn = account.fileNo.toLowerCase();
        if (fileNoMatchMode === 'exact') {
          if (fn !== q) return false;
        } else if (!fn.includes(q)) {
          return false;
        }
      }
      const b = Number(account.balance);
      const z = isZeroBalance(account.balance);
      if (balanceFilter === 'zero' && !z) return false;
      if (balanceFilter === 'nonzero' && z) return false;

      let lo = parseOptionalAmount(balanceMin);
      let hi = parseOptionalAmount(balanceMax);
      if (lo !== null && hi !== null && lo > hi) {
        const swap = lo;
        lo = hi;
        hi = swap;
      }
      if (lo !== null && b < lo) return false;
      if (hi !== null && b > hi) return false;
      return true;
    },
    [searchQuery, fileNoMatchMode, balanceFilter, balanceMin, balanceMax]
  );

  const filteredAccounts = accounts.filter(accountMatchesSidebarFilters);

  useEffect(() => {
    if (!selectedAccount?.fileNo) return;
    const acc = accounts.find((a) => a.fileNo === selectedAccount.fileNo);
    if (!acc || !accountMatchesSidebarFilters(acc)) {
      setSelectedAccount(null);
      setAccountDetails(null);
    }
  }, [accounts, selectedAccount, accountMatchesSidebarFilters]);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const rangeActive =
    balanceMin.trim() !== '' || balanceMax.trim() !== '';
  const advancedAccountsFiltersActive =
    balanceFilter !== 'all' || rangeActive || fileNoMatchMode !== 'contains';
  const sidebarFiltersActive =
    searchQuery.trim() !== '' || advancedAccountsFiltersActive;

  const handleClearSidebarFilters = () => {
    setSearchQuery('');
    setBalanceFilter('all');
    setBalanceMin('');
    setBalanceMax('');
    setFileNoMatchMode('contains');
  };

  return (
    <div className="accounts-layout">
      {/* Accounts List Sidebar */}
      <div className="accounts-list-panel">
        <div style={{ marginBottom: '14px' }}>
          <h2 style={{ margin: '0 0 6px 0', fontSize: '18px', color: '#0f172a' }}>
            Accounts (
            {filteredAccounts.length}
            {sidebarFiltersActive && accounts.length !== filteredAccounts.length
              ? ` of ${accounts.length}`
              : ''}
            )
          </h2>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-neutral-text-muted)', lineHeight: 1.45 }}>
            Filter by file no. below. Open <strong>Advanced filters</strong> for match style, balance
            preset, and range.
          </p>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label htmlFor="accounts-file-no" style={advLabelStyle}>
            File no. (Ref)
          </label>
          <input
            id="accounts-file-no"
            type="text"
            placeholder="Filter by reference…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={advInputStyle}
            autoComplete="off"
          />
        </div>

        {advancedAccountsFiltersActive && !showAdvancedFilters && (
          <div
            style={{
              marginBottom: '10px',
              padding: '8px 10px',
              fontSize: '11px',
              color: '#1d4ed8',
              backgroundColor: '#eff6ff',
              borderRadius: '6px',
              border: '1px solid #bfdbfe',
            }}
          >
            Advanced filters are active (panel hidden).
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowAdvancedFilters((open) => !open)}
          aria-expanded={showAdvancedFilters}
          style={{
            width: '100%',
            marginBottom: '14px',
            padding: '10px 14px',
            fontSize: '14px',
            fontWeight: '600',
            color: '#334155',
            backgroundColor: 'var(--color-workspace-bg)',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          {showAdvancedFilters ? '▲ Hide advanced filters' : '▼ Show advanced filters'}
        </button>

        {showAdvancedFilters && (
          <div style={criteriaPanelStyle}>
            <div
              style={{
                fontSize: '11px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--color-neutral-text-muted)',
                marginBottom: '14px',
              }}
            >
              Advanced filters
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="accounts-file-no-mode" style={advLabelStyle}>
                File no. match
              </label>
              <select
                id="accounts-file-no-mode"
                value={fileNoMatchMode}
                onChange={(e) => setFileNoMatchMode(e.target.value)}
                style={{ ...advInputStyle, cursor: 'pointer', backgroundColor: 'white' }}
              >
                <option value="contains">Contains (partial)</option>
                <option value="exact">Exact match</option>
              </select>
            </div>

            <div
              style={{
                borderTop: '1px solid var(--color-border)',
                paddingTop: '14px',
                marginBottom: '14px',
              }}
            >
              <label htmlFor="accounts-balance-filter" style={advLabelStyle}>
                Balance preset
              </label>
              <select
                id="accounts-balance-filter"
                value={balanceFilter}
                onChange={(e) => setBalanceFilter(e.target.value)}
                style={{ ...advInputStyle, cursor: 'pointer', backgroundColor: 'white' }}
              >
                <option value="all">All balances</option>
                <option value="zero">Zero balance</option>
                <option value="nonzero">Non-zero balance</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <span style={{ ...advLabelStyle, marginBottom: '8px' }}>Balance range (£)</span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="accounts-balance-min"
                    style={{ display: 'block', fontSize: '11px', color: 'var(--color-neutral-text-muted)', marginBottom: '4px' }}
                  >
                    Min
                  </label>
                  <input
                    id="accounts-balance-min"
                    type="text"
                    inputMode="decimal"
                    placeholder="Any"
                    value={balanceMin}
                    onChange={(e) => setBalanceMin(e.target.value)}
                    style={advInputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    htmlFor="accounts-balance-max"
                    style={{ display: 'block', fontSize: '11px', color: 'var(--color-neutral-text-muted)', marginBottom: '4px' }}
                  >
                    Max
                  </label>
                  <input
                    id="accounts-balance-max"
                    type="text"
                    inputMode="decimal"
                    placeholder="Any"
                    value={balanceMax}
                    onChange={(e) => setBalanceMax(e.target.value)}
                    style={advInputStyle}
                  />
                </div>
              </div>
              <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#94a3b8', lineHeight: 1.4 }}>
                Optional. Negative balances allowed. If min exceeds max, bounds are swapped.
              </p>
            </div>

            <div
              style={{
                borderTop: '1px solid var(--color-border)',
                paddingTop: '12px',
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClearSidebarFilters}
                style={{ width: '100%' }}
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}

        {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading accounts…</div>}

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '15px' }}>{error}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredAccounts.map((account) => (
            <div
              key={account.fileNo}
              onClick={() => handleAccountClick(account)}
              style={{
                padding: '15px',
                backgroundColor:
                  selectedAccount?.fileNo === account.fileNo
                    ? 'var(--color-primary-brand)'
                    : 'var(--color-surface)',
                color: selectedAccount?.fileNo === account.fileNo ? '#fff' : 'var(--color-neutral-text-main)',
                borderRadius: 'var(--radius-ui)',
                cursor: 'pointer',
                border: '1px solid var(--color-border)',
                transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                boxShadow:
                  selectedAccount?.fileNo === account.fileNo
                    ? '0 2px 8px rgba(173, 45, 45, 0.25)'
                    : 'none',
              }}
              onMouseEnter={(e) => {
                if (selectedAccount?.fileNo !== account.fileNo) {
                  e.currentTarget.style.backgroundColor = 'var(--color-workspace-bg)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedAccount?.fileNo !== account.fileNo) {
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                }
              }}
            >
              <div style={{ fontWeight: '600', marginBottom: '5px' }}>{account.fileNo}</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                Balance: {formatMoney(account.balance)}
              </div>
              <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '3px' }}>
                {account.transactionCount} transactions
              </div>
            </div>
          ))}
        </div>

        {filteredAccounts.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-neutral-text-muted)', fontSize: '14px' }}>
            {accounts.length === 0
              ? 'No accounts available.'
              : 'No accounts match your criteria.'}
          </div>
        )}
      </div>

      {/* Account Details */}
      <div className="accounts-detail-panel">
        {!selectedAccount ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h3 className="text-muted" style={{ marginBottom: '10px' }}>Select an account to view details</h3>
            <p className="text-muted" style={{ fontSize: '14px' }}>
              Click on any account from the list to see transactions and balance
            </p>
          </div>
        ) : (
          <div>
            {/* Account Header */}
            <div className="card" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 className="text-body" style={{ margin: 0, fontSize: '20px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {selectedAccount.fileNo}
                  </h2>
                  <p className="text-muted" style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                    Account Details
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedAccount(null);
                    setAccountDetails(null);
                  }}
                  className="btn btn-cancel btn-small"
                >
                  Close
                </button>
              </div>

              {/* Filters */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500' }}>Filter:</label>
                  <label>
                    <input
                      type="radio"
                      value="all"
                      checked={filterType === 'all'}
                      onChange={(e) => setFilterType(e.target.value)}
                    />
                    All
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="year"
                      checked={filterType === 'year'}
                      onChange={(e) => setFilterType(e.target.value)}
                    />
                    By Year
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="month"
                      checked={filterType === 'month'}
                      onChange={(e) => setFilterType(e.target.value)}
                    />
                    By Month
                  </label>
                  <label>
                    <input
                      type="radio"
                      value="dateRange"
                      checked={filterType === 'dateRange'}
                      onChange={(e) => setFilterType(e.target.value)}
                    />
                    Date Range
                  </label>
                </div>

                {filterType === 'year' && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      <option value="">Select Year</option>
                      {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                    <button onClick={handleFilterChange} className="btn btn-primary" style={{ padding: '8px 16px' }}>
                      Apply
                    </button>
                  </div>
                )}

                {filterType === 'month' && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <select
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      <option value="">Select Year</option>
                      {years.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                    <select
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      <option value="">Select Month</option>
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April</option>
                      <option value="5">May</option>
                      <option value="6">June</option>
                      <option value="7">July</option>
                      <option value="8">August</option>
                      <option value="9">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                    <button onClick={handleFilterChange} className="btn btn-primary" style={{ padding: '8px 16px' }}>
                      Apply
                    </button>
                  </div>
                )}

                {filterType === 'dateRange' && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                    <button onClick={handleFilterChange} className="btn btn-primary" style={{ padding: '8px 16px' }}>
                      Apply
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Summary Cards */}
            {accountDetails && (
              <>
                <div className="summary" style={{ marginBottom: '20px' }}>
                  <div className="summary-card">
                    <h3>Total Incoming</h3>
                    <div className="value amount-positive">
                      {formatMoney(accountDetails.summary.totalIncoming)}
                    </div>
                  </div>
                  <div className="summary-card">
                    <h3>Total Outgoing</h3>
                    <div className="value amount-negative">
                      {formatMoney(accountDetails.summary.totalOutgoing)}
                    </div>
                  </div>
                  <div className="summary-card">
                    <h3>Net Balance</h3>
                    <div
                      className={`value ${accountDetails.summary.netAmount >= 0
                          ? 'amount-positive'
                          : 'amount-negative'
                        }`}
                    >
                      {formatMoney(accountDetails.summary.netAmount)}
                    </div>
                  </div>
                  <div className="summary-card">
                    <h3>Total Transactions</h3>
                    <div className="value">{accountDetails.summary.totalTransactions}</div>
                  </div>
                </div>

                {/* Monthly Summary */}
                {accountDetails.monthlySummary && accountDetails.monthlySummary.length > 0 && (
                  <div className="card" style={{ marginBottom: '20px' }}>
                    <h3 style={{ marginBottom: '8px', fontSize: '18px', fontWeight: '600' }}>
                      Monthly Summary
                    </h3>
                    <p style={{ margin: '0 0 14px 0', fontSize: '12px', color: 'var(--color-neutral-text-muted)', lineHeight: 1.45 }}>
                      <strong>End of month balance</strong> is this file&apos;s running total after the last
                      transaction in that month (full ledger). Incoming/outgoing columns use only
                      transactions matching your current filter.
                    </p>
                    <div className="table-scroll">
                      <table className="table">
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th>Month</th>
                          <th>Incoming</th>
                          <th>Outgoing</th>
                          <th>End of month balance</th>
                          <th>Transactions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accountDetails.monthlySummary.map((month, index) => (
                          <tr key={index}>
                            <td>{month.year}</td>
                            <td>
                              {new Date(month.year, month.month - 1).toLocaleString('default', {
                                month: 'long',
                              })}
                            </td>
                            <td className="amount-positive">
                              {formatMoney(month.incoming)}
                            </td>
                            <td className="amount-negative">
                              {formatMoney(month.outgoing)}
                            </td>
                            <td
                              className={
                                month.endOfMonthBalance >= 0
                                  ? 'amount-positive'
                                  : 'amount-negative'
                              }
                            >
                              {formatMoney(month.endOfMonthBalance)}
                            </td>
                            <td>{month.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}

                {/* Transactions Table */}
                <div className="card">
                  <h3 style={{ marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>
                    Transactions ({accountDetails.entries.length})
                  </h3>
                  {detailsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>Loading transactions...</div>
                  ) : accountDetails.entries.length === 0 ? (
                    <p>No transactions found for the selected filter</p>
                  ) : (
                    <div className="table-scroll table-scroll--max-500">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>No</th>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Balance</th>
                            <th>Transfer ID</th>
                            <th>Remarks</th>
                            <th>Reason for payment</th>
                            <th>Year</th>
                            <th>Month</th>
                          </tr>
                        </thead>
                        <tbody>
                          {accountDetails.entries.map((entry) => {
                            const isPositive = entry.type === 'incoming' || entry.type === 'transfer_in';
                            const isTransfer = entry.type === 'transfer_in' || entry.type === 'transfer_out';

                            return (
                              <tr key={entry.id}>
                                <td>
                                  {entry.transactionDate
                                    ? new Date(entry.transactionDate).toLocaleDateString()
                                    : new Date(entry.createdAt).toLocaleDateString()}
                                </td>
                                <td>{entry.no || '-'}</td>
                                <td>
                                  <span
                                    className={
                                      isPositive
                                        ? isTransfer
                                          ? 'badge badge-transfer-in'
                                          : 'badge badge-incoming'
                                        : isTransfer
                                          ? 'badge badge-transfer-out'
                                          : 'badge badge-outgoing'
                                    }
                                  >
                                    {entry.type.replace('_', ' ')}
                                  </span>
                                </td>
                                <td
                                  className={
                                    isPositive
                                      ? 'amount-positive'
                                      : 'amount-negative'
                                  }
                                >
                                  {formatMoney(entry.amount)}
                                </td>
                                <td
                                  className={
                                    entry.balance >= 0 ? 'amount-positive' : 'amount-negative'
                                  }
                                  style={{ fontWeight: '600' }}
                                >
                                  {formatMoney(entry.balance)}
                                </td>
                                <td className="text-muted" style={{ fontSize: '12px' }}>
                                  {entry.transferId || '-'}
                                </td>
                                <td className="text-muted" style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {entry.remarks || '-'}
                                </td>
                                <td className="text-muted" style={{ fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }} title={entry.reasonForPayment || ''}>
                                  {entry.reasonForPayment || '-'}
                                </td>
                                <td>{entry.year}</td>
                                <td>
                                  {new Date(entry.year, entry.month - 1).toLocaleString('default', {
                                    month: 'long',
                                  })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Accounts;

